import type {MouseEvent, ReactElement} from 'react';
import {useState} from 'react';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import type {BillingAddressListItemProps} from '@Components/checkout-flow/components/billing-address-list-item/billing-address-list-item.types';
import {getBillingAddressSubText} from '@Components/checkout-flow/components/billing-address-list-item/billing-address-list-item.library';
import styles from './billing-address-list-item.module.scss';

export function BillingAddressListItem({address, isSelected, isDeleting, onSelect, onDelete}: BillingAddressListItemProps): ReactElement {
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const mainText = address.streetAddress2 !== '' ? `${address.streetAddress1}, ${address.streetAddress2}` : address.streetAddress1;

  const stopAndRun = (action: () => void): ((event: MouseEvent) => void) => {
    return (event: MouseEvent): void => {
      event.preventDefault();
      event.stopPropagation();
      action();
    };
  };

  const getSelectionIcon = (): ReactElement => {
    if (isSelected) {
      return <Icon icon="icon-checkbox-checked" size={IconSize.SIZE_ICON_32} type={IconType.FLAT} flatIconType={{color: FlatIconColor.PRIMARY, type: FlatIconColorType.DEFAULT}} className={styles.checkbox} ariaHidden />;
    }
    return <Icon icon="icon-checkbox-unchecked" size={IconSize.SIZE_ICON_32} className={`${styles.checkbox} ${styles.checkboxUnselected}`} ariaHidden />;
  };

  const getDeleteControls = (): ReactElement => {
    if (isConfirmingDelete) {
      return (
        <div className={styles.deleteControls}>
          <Icon icon="icon-check" size={IconSize.SIZE_ICON_20} onClick={stopAndRun(onDelete)} isUsedAsButton ariaLabel={window.i18next.t('pmwjs_confirm')} className={styles.option} />
          <Icon
            icon="icon-close"
            size={IconSize.SIZE_ICON_20}
            onClick={stopAndRun((): void => {
              setIsConfirmingDelete(false);
            })}
            isUsedAsButton
            ariaLabel={window.i18next.t('pmwjs_cancel')}
            className={styles.option}
          />
        </div>
      );
    }
    return (
      <div className={styles.deleteControls}>
        <Icon
          icon="icon-delete"
          size={IconSize.SIZE_ICON_20}
          onClick={stopAndRun((): void => {
            setIsConfirmingDelete(true);
          })}
          isUsedAsButton
          ariaLabel={window.i18next.t('pmwjs_delete')}
          className={styles.option}
        />
      </div>
    );
  };

  return (
    <li>
      <ClickableDiv
        onClick={onSelect}
        className={`${styles.item} ${isSelected ? styles.selected : ''} ${isDeleting ? styles.deleting : ''}`}
        role="radio"
        aria-checked={isSelected}
        aria-label={mainText}
      >
        {getSelectionIcon()}
        <div className={styles.texts}>
          <Text val={mainText} size={TextSize.MD} bold className={styles.mainText} />
          <Text val={getBillingAddressSubText(address)} size={TextSize.SM} className={styles.subText} />
        </div>
        {getDeleteControls()}
      </ClickableDiv>
    </li>
  );
}
